/**
 * Global axios auth interceptors.
 *
 * - Attaches the latest `auth_token` from localStorage to every backend call.
 * - On 401 from a protected endpoint, silently refreshes the token via
 *   POST /api/auth/refresh-token, retries the original request, and on
 *   failure clears the session + redirects to /login.
 *
 * This module installs the interceptors on the *default* axios instance so
 * every existing `axios.get(...)` / `axios.post(...)` call across the app
 * benefits automatically — no call-site changes needed.
 *
 * Import this file once (it's currently re-exported via AuthContext).
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

// Endpoints where 401 must NOT trigger a refresh attempt — they are the
// refresh / login endpoints themselves, or VDR external party flows that
// run on their own scoped token.
const NO_REFRESH = [
  '/api/auth/refresh-token',
  '/api/auth/send-otp',
  '/api/auth/verify-otp',
  '/api/auth/google/token',
  '/api/auth/google/callback',
  '/api/vdr/',          // external party portal — uses scoped contact tokens
];

const isAppRequest = (url = '') => {
  if (!url) return false;
  if (url.startsWith('http')) return url.startsWith(BACKEND_URL);
  return url.startsWith('/api/') || url.includes('/api/');
};

const shouldSkip = (url = '') => NO_REFRESH.some((p) => url.includes(p));

// ── Response interceptor — single-flight refresh on 401 ────────────────────
let refreshInFlight = null;

const doRefresh = async () => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t) return null;
  try {
    const r = await axios.post(
      `${BACKEND_URL}/api/auth/refresh-token`,
      {},
      { headers: { Authorization: `Bearer ${t}` }, _skipAuthRefresh: true },
    );
    const fresh = r.data?.token;
    const u = r.data?.user;
    if (fresh) localStorage.setItem(TOKEN_KEY, fresh);
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    return fresh;
  } catch (e) {
    return null;
  }
};

axios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || '';

    if (
      status !== 401
      || original._retried
      || original._skipAuthRefresh
      || shouldSkip(url)
      || !isAppRequest(url)
    ) {
      return Promise.reject(error);
    }

    original._retried = true;
    if (!refreshInFlight) refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
    const fresh = await refreshInFlight;

    if (!fresh) {
      // Refresh failed → wipe session and bounce to login.
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`/login?next=${next}`);
      }
      return Promise.reject(error);
    }

    // Retry the original request with the fresh token.
    original.headers = original.headers || {};
    original.headers.Authorization = `Bearer ${fresh}`;
    return axios(original);
  },
);

export default axios;

